import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { Upload } from 'lucide-react';
import { useCallback, useRef, useState, type FC, type ReactNode } from 'react';

interface DropzoneProps {
  onFilesSelected: (files: File[]) => void;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  icon?: ReactNode;
  accept?: string;
  multiple?: boolean;
  className?: string;
}

export const Dropzone: FC<DropzoneProps> = ({
  onFilesSelected,
  isLoading = false,
  title = 'Drop files here',
  subtitle = 'or click to browse and upload files',
  buttonText = 'Browse files',
  icon,
  accept,
  multiple = true,
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      onFilesSelected(files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFilesSelected]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    handleFileSelect(files);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      handleFileSelect(files);
    },
    [handleFileSelect]
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleInputChange}
        accept={accept}
        multiple={multiple}
      />
      <FlexCol
        p="8"
        className={`border-2 border-dashed rounded-lg bg-white w-full min-h-[300px] transition-colors ${
          isDragging ? 'border-primary-500 bg-primary-50' : 'border-neutral-200'
        } ${className}`}
        alignItems="center"
        justifyContent="center"
        gap="4"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {icon ?? (
          <Upload
            className={`h-10 w-10 rounded-md p-3 ${
              isDragging ? 'bg-primary-100' : 'bg-neutral-100'
            }`}
          />
        )}
        <FlexCol gap="2" alignItems="center" justifyContent="center">
          <Text variant="body-sm-bold">{title}</Text>
          <Text className="text-gray-500" variant="body-sm">
            {subtitle}
          </Text>
        </FlexCol>
        <Button
          variant="outline"
          onClick={handleUploadClick}
          disabled={isLoading}
          loading={isLoading}
        >
          <Upload size={16} />
          {buttonText}
        </Button>
      </FlexCol>
    </>
  );
};
